import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Link,
  Alert,
  Stack,
  Card,
  CardContent,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Modal,
  IconButton
} from '@mui/material';
import {
  CalendarMonth,
  BarChart,
  CheckCircleOutline,
  Visibility,
  VisibilityOff,
  Share,
  TrendingUp,
  ExpandMore,
  Close
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import AuthContext from "../context/AuthContext";
import { useNavigate } from 'react-router-dom';
import logo from '../logo.png';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';

function LandingPage() {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { loginUser, backendUrl, user, emailVerified, loading: authLoading } = useContext(AuthContext);

  useEffect(() => {
    if (!authLoading && user && emailVerified) {
      navigate('/today');
    }
  }, [user, emailVerified, authLoading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setEmailNotVerified(false);
    setResendSuccess(false);

    try {
      await loginUser(email, password);
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('email not verified')) {
        setEmailNotVerified(true);
        setError("Your email must be verified to login. Please check your email for the verification link.");
      } else if (err.message === "No active account found with the given credentials") {
        setError("Invalid email or password. Please try again.");
      } else if (err.message.includes("NetworkError")) {
        setError("Network error. Please check your connection.");
      } else {
        setError(err.message || "An unexpected error occurred. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setError(null);
    setResendSuccess(false);

    try {
      const response = await fetch(`${backendUrl}api/auth/resend-verification/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send verification email');
      }

      setResendSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  const features = [
    {
      icon: <Visibility sx={{ fontSize: 48, color: '#7B6B8D' }} />,
      title: 'Make Invisible Work Visible',
      description: 'Track who does what in your relationship. See the real distribution of household chores, emotional labor, and daily contributions. No more guessing or arguing about who does more.'
    },
    {
      icon: <BarChart sx={{ fontSize: 48, color: '#C4856A' }} />,
      title: 'Data-Driven Relationship Insights',
      description: 'Beautiful charts and statistics show exactly where imbalances exist. Use data as your source of truth for conversations about fairness and equality in your partnership.'
    },
    {
      icon: <Share sx={{ fontSize: 48, color: '#A8D5BA' }} />,
      title: 'Track Solo or Share with Your Partner',
      description: 'Start tracking on your own to understand the real division of labor, then invite your partner when ready. Perfect for both accountability and transparency.'
    }
  ];

  const howItWorks = [
    {
      icon: <CalendarMonth sx={{ fontSize: 40, color: '#7B6B8D' }} />,
      title: 'Track Your Daily Contributions',
      description: 'Log chores, time-off, and other contributions as you do them. Quick and simple - takes just seconds per day.',
      borderColor: '#7B6B8D'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: '#C4856A' }} />,
      title: 'See the Real Balance',
      description: "View statistics and charts that show who's doing what. Identify patterns and imbalances in your relationship.",
      borderColor: '#C4856A'
    },
    {
      icon: <CheckCircleOutline sx={{ fontSize: 40, color: '#A8D5BA' }} />,
      title: 'Share or Keep Private',
      description: 'Use it as your personal source of truth, or invite your partner to track together. You decide what works for your relationship.',
      borderColor: '#A8D5BA'
    }
  ];

  const screenshots = [
    {
      src: '/screenshots/daily-tracking.png',
      alt: 'Daily tracking interface showing chores and time-off entries for both partners',
      title: 'Daily Tracking',
      description: 'Log chores and time-off in seconds'
    },
    {
      src: '/screenshots/calendar-view.png',
      alt: 'Calendar view showing daily chores and time-off tracking',
      title: 'Monthly Overview',
      description: 'See patterns over time'
    },
    {
      src: '/screenshots/statistics-charts.png',
      alt: 'Statistical charts comparing chore distribution between partners',
      title: 'Visual Statistics',
      description: 'Data-driven insights'
    },
    {
      src: '/screenshots/relationship-timeline.png',
      alt: 'Relationship timeline showing partnership history',
      title: 'Relationship Timeline',
      description: 'Track your journey together'
    }
  ];

  const faqs = [
    {
      question: 'Do both partners need to sign up?',
      answer: "No! You can start tracking on your own to see the real distribution of labor. When you're ready, you can invite your partner to join and track together. Many users start solo to establish a baseline of reality before sharing."
    },
    {
      question: 'Can my partner see my data?',
      answer: 'Your data is completely private by default. Even if your partner also uses Couplendar, they will never see your tracking unless you explicitly decide to share it with them. You have full control over what you share and when.'
    },
    {
      question: 'Is this just for couples who fight about chores?',
      answer: "Not at all. Couplendar is for any couple who wants transparency and equality. Whether you're struggling with imbalance or just want to maintain fairness, having data helps. Many users discover invisible patterns they never noticed before."
    },
    {
      question: 'How is this different from a regular task app?',
      answer: "Couplendar is specifically designed to track relationship equality, not just task completion. It focuses on who does what, visualizes balance between partners, and addresses the mental load. It's about fairness, not productivity."
    },
    {
      question: 'What can I track?',
      answer: 'Track household chores (dishes, laundry, cooking), childcare, emotional labor, time-off (sick days, personal time), errands, and any other contributions to your shared life. You define what matters in your relationship.'
    }
  ];

  return (
    <>
      <SEO
        title="Couplendar - Track Chores & Build Equal Relationships for Couples"
        description="Track daily chores, time-off, and contributions with your partner. Build stronger, more balanced relationships through transparency and shared responsibility."
        keywords="couple chore tracker, relationship balance app, shared responsibility, household chores, couple calendar, relationship equality, domestic labor tracker, mental load, fair division of labor, chore tracking app for couples"
        url="https://couplendar.com/"
      />
      <StructuredData
        data={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Couplendar",
          "applicationCategory": "LifestyleApplication",
          "description": "Track daily chores, time-off, and contributions with your partner. Build stronger, more balanced relationships through transparency and shared responsibility.",
          "url": "https://couplendar.com",
          "author": { "@type": "Organization", "name": "Couplendar" },
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          "featureList": [
            "Track daily activities and chores",
            "Visual statistics and charts",
            "Build relationship equality",
            "Monitor time-off balance",
            "Promote shared responsibility"
          ],
          "screenshot": "https://couplendar.com/screenshots/daily-tracking.png",
          "operatingSystem": "Web browser, iOS, Android",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "150"
          }
        }}
      />
      <Box sx={{ minHeight: '100vh', backgroundColor: '#F9F7F4', py: { xs: 2, sm: 3, md: 6 } }}>
        <Container maxWidth="lg">

          {/* Hero Section */}
          <Box component="section" textAlign="center" mb={{ xs: 4, sm: 6, md: 10 }} mt={{ xs: 2, sm: 3, md: 4 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
              <Box
                component="img"
                src={logo}
                alt="Couplendar"
                sx={{ width: 'auto', height: { xs: '2rem', sm: '3rem', md: '4.5rem' }, objectFit: 'contain', mr: { xs: 1.5, sm: 2, md: 2.5 } }}
              />
              <Typography
                component="h1"
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: '#7B6B8D',
                  fontSize: { xs: '2rem', sm: '3rem', md: '4.5rem' }
                }}
              >
                Couplendar
              </Typography>
            </Box>

            <Typography
              component="h2"
              variant="h3"
              sx={{
                color: '#2A2535',
                fontWeight: 600,
                maxWidth: 900,
                mx: 'auto',
                mb: { xs: 2, sm: 3, md: 4 },
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                lineHeight: 1.3
              }}
            >
              Is Your Relationship Really Equal?
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: '#5C5349',
                fontWeight: 400,
                maxWidth: 700,
                mx: 'auto',
                mb: { xs: 3, sm: 4, md: 5 },
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                lineHeight: 1.6
              }}
            >
              Track who does what in your relationship. Make invisible labor visible. Use data as your source of truth for building a more balanced partnership.
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                borderRadius: 2,
                py: { xs: 1.75, sm: 2 },
                px: { xs: 4, sm: 6 },
                fontSize: { xs: '1rem', sm: '1.125rem' },
                fontWeight: 600,
                backgroundColor: '#C4856A',
                boxShadow: '0 4px 20px rgba(196, 133, 106, 0.35)',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#B0745A',
                  boxShadow: '0 6px 24px rgba(196, 133, 106, 0.45)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Start Tracking Today
            </Button>
          </Box>

          {/* Features Grid */}
          <Box component="section" sx={{ mb: { xs: 6, sm: 8, md: 12 } }}>
            <Typography
              component="h3"
              variant="h4"
              textAlign="center"
              sx={{ mb: { xs: 3, sm: 4, md: 6 }, fontWeight: 600, color: '#2A2535', fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}
            >
              Why couples use Couplendar
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 3, md: 4 } }}>
              {features.map((feature, index) => (
                <Card
                  key={index}
                  sx={{
                    background: 'white',
                    border: '1px solid #E5DDD5',
                    boxShadow: '0 2px 12px rgba(42,37,53,0.04)',
                    borderRadius: 3,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(42,37,53,0.08)' }
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                    <Box mb={2}>{feature.icon}</Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2A2535', mb: 1.5, fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5C5349', lineHeight: 1.7, fontSize: { xs: '0.9375rem', md: '1rem' } }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* How It Works */}
          <Box
            component="section"
            sx={{
              mb: { xs: 6, sm: 8, md: 12 },
              background: 'white',
              borderRadius: 4,
              py: { xs: 4, sm: 6, md: 8 },
              px: { xs: 2, sm: 3, md: 4 },
              boxShadow: '0 2px 20px rgba(42,37,53,0.04)',
              border: '1px solid #E5DDD5'
            }}
          >
            <Typography
              component="h3"
              variant="h4"
              textAlign="center"
              sx={{ mb: { xs: 3, sm: 4, md: 6 }, fontWeight: 600, color: '#2A2535', fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}
            >
              How It Works
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 4, md: 6 }, maxWidth: 1000, mx: 'auto' }}>
              {howItWorks.map((step, index) => (
                <Box key={index} sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: '#F9F7F4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      border: '3px solid',
                      borderColor: step.borderColor
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2A2535', mb: 1.5, fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#5C5349', lineHeight: 1.7, fontSize: { xs: '0.9375rem', md: '1rem' } }}>
                    {step.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Screenshots Section */}
          <Box component="section" sx={{ mb: { xs: 6, sm: 8, md: 12 } }}>
            <Typography
              component="h3"
              variant="h4"
              textAlign="center"
              sx={{ mb: { xs: 3, sm: 4, md: 6 }, fontWeight: 600, color: '#2A2535', fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}
            >
              See Couplendar in Action
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 2.5, md: 3 } }}>
              {screenshots.map((screenshot, index) => (
                <Card
                  key={index}
                  onClick={() => setSelectedImage(screenshot)}
                  sx={{
                    background: 'white',
                    boxShadow: '0 2px 12px rgba(42,37,53,0.06)',
                    border: '1px solid #E5DDD5',
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 6px 20px rgba(42,37,53,0.12)' }
                  }}
                >
                  <Box component="img" src={screenshot.src} alt={screenshot.alt} sx={{ width: '100%', height: 'auto', display: 'block' }} />
                  <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2A2535', mb: 0.5, fontSize: { xs: '0.875rem', md: '0.9375rem' } }}>
                      {screenshot.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#5C5349', lineHeight: 1.4, fontSize: { xs: '0.75rem', md: '0.8125rem' }, display: 'block' }}>
                      {screenshot.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Typography variant="body2" textAlign="center" sx={{ mt: 3, color: '#8B7E75', fontSize: { xs: '0.875rem', md: '0.9375rem' } }}>
              Click any screenshot to view larger
            </Typography>
          </Box>

          {/* Image Modal */}
          <Modal open={selectedImage !== null} onClose={() => setSelectedImage(null)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', outline: 'none' }}>
              <IconButton
                onClick={() => setSelectedImage(null)}
                sx={{ position: 'absolute', top: -16, right: -16, backgroundColor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', '&:hover': { backgroundColor: '#F5F5F5' }, zIndex: 1 }}
              >
                <Close />
              </IconButton>
              {selectedImage && (
                <Box sx={{ backgroundColor: 'white', borderRadius: 2, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                  <Box
                    component="img"
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    sx={{ width: '100%', height: 'auto', maxHeight: 'calc(90vh - 80px)', display: 'block', objectFit: 'contain' }}
                  />
                  <Box sx={{ p: 2.5, borderTop: '1px solid #E5DDD5' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2A2535', mb: 0.5 }}>{selectedImage.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#5C5349' }}>{selectedImage.alt}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Modal>

          {/* FAQ Section */}
          <Box component="section" sx={{ mb: { xs: 6, sm: 8, md: 12 }, maxWidth: 900, mx: 'auto' }}>
            <Typography
              component="h3"
              variant="h4"
              textAlign="center"
              sx={{ mb: { xs: 3, sm: 4, md: 6 }, fontWeight: 600, color: '#2A2535', fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}
            >
              Frequently Asked Questions
            </Typography>

            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                sx={{
                  mb: 2,
                  background: 'white',
                  boxShadow: '0 2px 8px rgba(42,37,53,0.04)',
                  border: '1px solid #E5DDD5',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#7B6B8D' }} />}
                  sx={{ '&:hover': { background: alpha('#7B6B8D', 0.03) } }}
                >
                  <Typography sx={{ fontWeight: 600, color: '#2A2535', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: '#5C5349', lineHeight: 1.7, fontSize: { xs: '0.9375rem', md: '1rem' } }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* SEO Content Section */}
          <Box component="section" sx={{ mb: { xs: 6, sm: 8, md: 10 }, maxWidth: 900, mx: 'auto' }}>
            <Typography
              component="h3"
              variant="h5"
              textAlign="center"
              sx={{ mb: 3, fontWeight: 600, color: '#2A2535', fontSize: { xs: '1.5rem', md: '1.75rem' } }}
            >
              Why use a relationship balance tracker?
            </Typography>
            <Box sx={{ color: '#5C5349', lineHeight: 1.8, fontSize: { xs: '0.9375rem', md: '1rem' } }}>
              <Typography paragraph>
                Many couples struggle with unequal distribution of household chores and the mental load. One partner often carries the invisible burden of planning, organizing, and remembering while the other remains unaware of the imbalance. Couplendar helps you track who does what, making invisible labor visible.
              </Typography>
              <Typography paragraph>
                Whether you're tracking dishes, laundry, cooking, childcare, or emotional labor, Couplendar gives you a clear picture of daily contributions. Use it as your personal source of truth to understand the real dynamics, or share it with your partner for complete transparency. Build a more balanced, equitable relationship with data-driven insights about your household responsibilities.
              </Typography>
            </Box>
          </Box>

          {/* Login Section */}
          <Box component="section" sx={{ maxWidth: 440, mx: 'auto', mb: { xs: 4, sm: 6, md: 8 } }}>
            <Card sx={{ background: 'white', boxShadow: '0 4px 24px rgba(42,37,53,0.06)', border: '1px solid #E5DDD5', borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                <Typography variant="h5" textAlign="center" sx={{ mb: { xs: 2, sm: 2.5, md: 3 }, fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600, color: '#2A2535' }}>
                  Already Have an Account?
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, fontSize: { xs: '0.875rem', sm: '0.9375rem' }, py: { xs: 0.5, sm: 1 }, '& .MuiAlert-icon': { color: '#C4856A' } }}>
                    {error}
                  </Alert>
                )}

                {resendSuccess && (
                  <Alert severity="success" sx={{ mb: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, fontSize: { xs: '0.875rem', sm: '0.9375rem' }, py: { xs: 0.5, sm: 1 }, '& .MuiAlert-icon': { color: '#A8D5BA' } }}>
                    Verification email sent! Please check your inbox. The link is valid for 5 minutes.
                  </Alert>
                )}

                <Box component="form" onSubmit={handleLogin}>
                  <Stack spacing={{ xs: 1.75, sm: 2, md: 2.5 }}>
                    <TextField
                      label="Email"
                      type="email"
                      variant="outlined"
                      fullWidth
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={!!error}
                    />
                    <TextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={!!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    {emailNotVerified && (
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        sx={{ borderRadius: 2, py: { xs: 1.25, sm: 1.5 }, fontSize: { xs: '0.9375rem', sm: '1rem' } }}
                      >
                        {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                      </Button>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      fullWidth
                      disabled={loading}
                      sx={{ borderRadius: 2, py: { xs: 1.25, sm: 1.5 }, fontSize: { xs: '0.9375rem', sm: '1rem' } }}
                    >
                      {loading ? 'Logging in...' : 'Login'}
                    </Button>
                  </Stack>

                  <Box textAlign="center" mt={{ xs: 2, sm: 2.5, md: 3 }}>
                    <Typography variant="body2" sx={{ color: '#8B7E75', fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                      Don't have an account?{' '}
                      <Link href="/register" sx={{ color: '#7B6B8D', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                        Sign up
                      </Link>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#8B7E75', mt: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                      <Link href="/forgot-password" sx={{ color: '#7B6B8D', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                        Forgot password?
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Footer */}
          <Typography
            variant="body2"
            textAlign="center"
            component="footer"
            sx={{ color: '#8B7E75', mt: { xs: 3, sm: 4, md: 6 }, mb: { xs: 2, sm: 3, md: 4 }, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}
          >
            Building healthy relationships through equality and transparency
          </Typography>

        </Container>
      </Box>
    </>
  );
}

export default LandingPage;
